import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { CopilotCitation } from '../../utils/api'
import { apiGet, getErrorMessage } from '../../utils/api'
import { CopilotMarkdown } from './CopilotMarkdown'
import { useForwarding } from '../../contexts/ForwardingContext'
import { toast } from '../shared/NotificationToast'

export interface DisplayAttachment {
  url: string
  type?: string
  name?: string
}

export interface DisplayMessage {
  id: number | string
  role: 'user' | 'assistant'
  content: string
  citations: CopilotCitation[] | null
  attachments?: DisplayAttachment[]
  /** True while assistant tokens are still streaming in. */
  streaming?: boolean
}

function attIsImage(a: DisplayAttachment): boolean {
  const t = (a.type || '').toLowerCase()
  return t === 'image' || t.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|tiff?)(\?|$)/i.test(a.url) || /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(a.name || '')
}

const CopyIcon: React.FC = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"
    />
  </svg>
)

const CheckIcon: React.FC = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
)

const ForwardIcon: React.FC = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8.688c0-.864.933-1.405 1.683-.977l7.108 4.062a1.125 1.125 0 010 1.953l-7.108 4.062A1.125 1.125 0 013 16.81V8.688zM12.75 8.688c0-.864.933-1.405 1.683-.977l7.108 4.062a1.125 1.125 0 010 1.953l-7.108 4.062a1.125 1.125 0 01-1.683-.977V8.688z" />
  </svg>
)

/** Minimal shape of a /queue/mine row used by the chat picker. */
interface OpenChatRow {
  id: string | number
  customerName?: string
  customer_name?: string
  customerId?: string | number
  customer_id?: string | number
  channel?: string
}

const SourcesIcon: React.FC = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
    />
  </svg>
)

const Sources: React.FC<{ citations: CopilotCitation[] }> = ({ citations }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-txt-tertiary hover:text-brand-600 transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <SourcesIcon />
        {citations.length} source{citations.length > 1 ? 's' : ''}
        <svg
          className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {citations.map((c, i) => (
            <span
              key={`${c.anchor}-${i}`}
              title={c.page_title}
              className="inline-flex items-center max-w-[16rem] truncate px-2 py-1 rounded-md text-[11px] font-medium bg-surface-tertiary text-txt-secondary border border-border"
            >
              {c.page_title}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

const StreamingDots: React.FC = () => (
  <span className="inline-flex items-center gap-1 align-middle ml-0.5" aria-label="Assistant is typing">
    <span className="w-1.5 h-1.5 rounded-full bg-txt-tertiary animate-bounce [animation-delay:-0.3s]" />
    <span className="w-1.5 h-1.5 rounded-full bg-txt-tertiary animate-bounce [animation-delay:-0.15s]" />
    <span className="w-1.5 h-1.5 rounded-full bg-txt-tertiary animate-bounce" />
  </span>
)

export const CopilotMessageBubble: React.FC<{ message: DisplayMessage }> = ({ message }) => {
  const [copied, setCopied] = useState(false)
  const navigate = useNavigate()
  const { returnTo, forwardToChat } = useForwarding()
  const isUser = message.role === 'user'

  const [pickerOpen, setPickerOpen] = useState(false)
  const [openChats, setOpenChats] = useState<OpenChatRow[] | null>(null)
  const [loadingChats, setLoadingChats] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.content)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard unavailable */
    }
  }

  // One-click forward back to the originating chat/ticket.
  const forwardToReturn = () => {
    if (!returnTo) return
    forwardToChat({ text: message.content, target: { kind: returnTo.kind, id: returnTo.id } })
    navigate(returnTo.path)
  }

  // No returnTo → let the agent pick one of their open chats.
  const togglePicker = async () => {
    setPickerOpen((v) => !v)
    if (openChats === null && !loadingChats) {
      setLoadingChats(true)
      try {
        const res = await apiGet<{ chats: OpenChatRow[] } | OpenChatRow[]>('/queue/mine', {
          status: 'open',
        })
        setOpenChats(Array.isArray(res) ? res : res.chats || [])
      } catch (err) {
        toast.error('Could not load chats', getErrorMessage(err, 'Please try again'))
        setOpenChats([])
      } finally {
        setLoadingChats(false)
      }
    }
  }

  const forwardToPicked = (row: OpenChatRow) => {
    const isTicket = row.channel === 'ticket'
    forwardToChat({
      text: message.content,
      target: { kind: isTicket ? 'ticket' : 'chat', id: row.id },
    })
    setPickerOpen(false)
    navigate(isTicket ? `/tickets/${row.id}` : `/queue/${row.id}`)
  }

  useEffect(() => {
    if (!pickerOpen) return
    const onDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPickerOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [pickerOpen])

  if (isUser) {
    const atts = message.attachments || []
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] flex flex-col items-end gap-1.5">
          {atts.length > 0 && (
            <div className="flex flex-wrap gap-1.5 justify-end">
              {atts.map((a, i) =>
                attIsImage(a) ? (
                  <a key={i} href={a.url} target="_blank" rel="noreferrer">
                    <img
                      src={a.url}
                      alt={a.name || 'attachment'}
                      className="max-w-[12rem] max-h-48 rounded-xl border border-border object-cover"
                    />
                  </a>
                ) : (
                  <a
                    key={i}
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-surface-secondary border border-border text-xs text-txt-secondary max-w-[14rem] truncate hover:bg-surface-hover"
                    title={a.name || a.url}
                  >
                    📎 {a.name || 'file'}
                  </a>
                )
              )}
            </div>
          )}
          {message.content && (
            <div className="rounded-2xl rounded-br-md bg-brand-600 text-white px-4 py-2.5 text-sm whitespace-pre-wrap break-words shadow-sm">
              {message.content}
            </div>
          )}
        </div>
      </div>
    )
  }

  const hasCitations = !!message.citations && message.citations.length > 0
  const showEmpty = message.streaming && message.content.length === 0

  return (
    <div className="group flex justify-start">
      <div className="max-w-full w-full">
        <div className="text-txt-primary text-sm leading-relaxed">
          {showEmpty ? (
            <StreamingDots />
          ) : (
            <>
              <CopilotMarkdown content={message.content} />
              {message.streaming && <StreamingDots />}
            </>
          )}
        </div>

        {!message.streaming && hasCitations && <Sources citations={message.citations!} />}

        {!message.streaming && message.content.length > 0 && (
          <div
            ref={pickerRef}
            className="relative mt-1.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
          >
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1 text-xs text-txt-tertiary hover:text-brand-600 transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 px-1 py-0.5"
              title="Copy message"
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
              {copied ? 'Copied' : 'Copy'}
            </button>

            {returnTo ? (
              <button
                type="button"
                onClick={forwardToReturn}
                className="inline-flex items-center gap-1 text-xs text-txt-tertiary hover:text-brand-600 transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 px-1 py-0.5"
                title={`Forward to ${returnTo.label}`}
              >
                <ForwardIcon />
                <span className="max-w-[12rem] truncate">Forward to {returnTo.label}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={togglePicker}
                aria-haspopup="menu"
                aria-expanded={pickerOpen}
                className="inline-flex items-center gap-1 text-xs text-txt-tertiary hover:text-brand-600 transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 px-1 py-0.5"
                title="Forward to a chat"
              >
                <ForwardIcon />
                Forward to chat
              </button>
            )}

            {pickerOpen && !returnTo && (
              <div
                role="menu"
                className="absolute z-30 top-full mt-1 left-0 w-64 max-h-72 overflow-y-auto rounded-lg border border-border bg-surface-elevated shadow-float p-1 animate-slide-up"
              >
                <p className="px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">
                  Forward to one of your chats
                </p>
                {loadingChats ? (
                  <p className="px-2.5 py-2 text-xs text-txt-tertiary">Loading chats…</p>
                ) : openChats && openChats.length > 0 ? (
                  openChats.map((row) => {
                    const name =
                      row.customerName ||
                      row.customer_name ||
                      String(row.customerId ?? row.customer_id ?? row.id)
                    return (
                      <button
                        key={String(row.id)}
                        type="button"
                        role="menuitem"
                        onClick={() => forwardToPicked(row)}
                        className="w-full text-left px-2.5 py-2 rounded-md text-sm text-txt-primary hover:bg-surface-hover transition-colors truncate"
                        title={name}
                      >
                        {name}
                      </button>
                    )
                  })
                ) : (
                  <p className="px-2.5 py-2 text-xs text-txt-tertiary">No open chats</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
